import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { CircularProgress, Alert, Button, Tabs, Tab, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import MostViewedProjects from '../components/admin/MostViewedProjects';
import VisitStats from '../components/admin/VisitStats';
import ProjectStats from '../components/admin/ProjectStats';
import SystemMetrics from '../components/admin/SystemMetrics';
import ProjectsTab from '../components/admin/ProjectsTab';
import './AdminPanel.css';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? window.location.origin
  : (process.env.REACT_APP_API_URL || 'http://localhost:3001');

const AdminPanel = () => {
    const { token, logout } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [stats, setStats] = useState({
        daily: { totalVisits: 0, uniqueVisitors: 0 },
        monthly: { totalVisits: 0, uniqueVisitors: 0 },
        annual: { totalVisits: 0, uniqueVisitors: 0 }
    });
    const [totalProjects, setTotalProjects] = useState(0);

    // Vérification immédiate de l'authentification
    useEffect(() => {
        if (!token) {
            navigate('/admin/login', { replace: true });
        }
    }, [token, navigate]);

    // Chargement des statistiques uniquement si authentifié
    useEffect(() => {
        if (!token) return;

        const fetchStats = async () => {
            try {
                setLoading(true);
                setError(null);

                // Récupérer les statistiques de visites
                const visitsResponse = await fetch(`${API_BASE_URL}/api/stats/visits`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!visitsResponse.ok) {
                    if (visitsResponse.status === 401) {
                        // Si le token n'est plus valide, déconnecter l'utilisateur
                        logout();
                        navigate('/admin/login', { replace: true });
                        return;
                    }
                    throw new Error('Erreur lors de la récupération des statistiques de visites');
                }

                const visitsData = await visitsResponse.json();
                setStats({
                    daily: visitsData.daily || { totalVisits: 0, uniqueVisitors: 0 },
                    monthly: visitsData.monthly || { totalVisits: 0, uniqueVisitors: 0 },
                    annual: visitsData.annual || { totalVisits: 0, uniqueVisitors: 0 }
                });

                // Récupérer le nombre total de projets
                const projectsResponse = await fetch(`${API_BASE_URL}/api/stats/projects/count`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!projectsResponse.ok) {
                    if (projectsResponse.status === 401) {
                        // Si le token n'est plus valide, déconnecter l'utilisateur
                        logout();
                        navigate('/admin/login', { replace: true });
                        return;
                    }
                    throw new Error('Erreur lors de la récupération du nombre de projets');
                }

                const projectsData = await projectsResponse.json();
                setTotalProjects(projectsData.total || 0);

            } catch (err) {
                console.error('Erreur lors du chargement des statistiques:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, [token, navigate, logout]);

    const handleLogout = () => {
        logout();
        navigate('/admin/login', { replace: true });
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    // Si pas de token, ne rien afficher pendant la redirection
    if (!token) {
        return null;
    }

    if (loading) {
        return (
            <div className="admin-panel-container">
                <div className="loading-container">
                    <CircularProgress />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-panel-container">
                <Alert severity="error">{error}</Alert>
            </div>
        );
    }

    return (
        <div className="admin-panel-container">
            <div className="admin-header">
                <h1>Tableau de bord administrateur</h1>
                <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleLogout}
                    className="logout-button"
                >
                    Se déconnecter
                </Button>
            </div>
            
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs 
                    value={activeTab} 
                    onChange={handleTabChange}
                    aria-label="onglets du tableau de bord"
                    className="admin-tabs"
                >
                    <Tab label="Statistiques" />
                    <Tab label="Projets" />
                    <Tab label="Technique" />
                </Tabs>
            </Box>

            <div className="tab-content-wrapper">
                {activeTab === 0 && (
                    <div className="stats-grid">
                        <VisitStats title="Aujourd'hui" stats={stats.daily} />
                        <VisitStats title="Ce mois" stats={stats.monthly} />
                        <VisitStats title="Cette année" stats={stats.annual} />
                        <ProjectStats totalProjects={totalProjects} />
                        <MostViewedProjects />
                    </div>
                )}

                {activeTab === 1 && (
                    <ProjectsTab />
                )}

                {activeTab === 2 && (
                    <div className="stats-grid">
                        <SystemMetrics />
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminPanel; 